import { apiRequest } from '../../../shared/services/apiClient';
import type {
  AppointmentType,
  AppointmentTypeListFilters,
  CreateAppointmentTypeInput,
  UpdateAppointmentTypeInput,
} from '../../calendar/types/Calendar';
import type {
  AddConsultationFieldValueInput,
  AddConsultationFieldAssignmentInput,
  AddConsultationTemplateFieldInput,
  Consultation,
  ConsultationFieldDefinition,
  ConsultationFieldDefinitionListFilters,
  ConsultationFieldAssignment,
  ConsultationStatusDefinition,
  ConsultationFieldValue,
  ConsultationListFilters,
  ConsultationTemplate,
  ConsultationTemplateField,
  ConsultationTemplateListFilters,
  CreateConsultationFieldDefinitionInput,
  CreateConsultationTemplateInput,
  CreateConsultationInput,
  PagedConsultationResult,
  UpdateConsultationFieldDefinitionInput,
  UpdateConsultationTemplateFieldInput,
  UpdateConsultationTemplateInput,
  UpdateConsultationFieldValueInput,
  UpdateConsultationInput,
} from '../types/Consultation';

const pickItems = <T>(payload: any): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (Array.isArray(payload?.items)) return payload.items as T[];
  if (Array.isArray(payload?.data)) return payload.data as T[];
  if (Array.isArray(payload?.data?.items)) return payload.data.items as T[];
  return [];
};

const pickSingle = <T>(payload: any): T | null => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object') return payload.data as T;
  return payload as T;
};

const pickPaged = <T>(payload: any, normalizeItem: (value: any) => T, fallbackPage = 1, fallbackPageSize = 25) => ({
  items: pickItems<any>(payload).map(normalizeItem),
  page: typeof payload?.page === 'number' ? payload.page : fallbackPage,
  pageSize: typeof payload?.pageSize === 'number' ? payload.pageSize : fallbackPageSize,
  total: typeof payload?.total === 'number' ? payload.total : 0,
  hasNext: Boolean(payload?.hasNext),
});

const toQueryString = (params: Record<string, string | number | boolean | undefined>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const rendered = qs.toString();
  return rendered ? `?${rendered}` : '';
};

const normalizeConsultationFieldType = (value: any): ConsultationFieldDefinition['fieldType'] => {
  if (typeof value === 'string') {
    const normalized = value.trim();
    const lower = normalized.toLowerCase();
    if (lower === 'longtext') return 'longText';
    if (lower === 'datetime') return 'dateTime';
    if (lower === 'singleselect') return 'singleSelect';
    if (lower === 'multiselect') return 'multiSelect';
    if (lower === 'repeater') return 'repeater';
    if (lower === 'attachments') return 'attachments';
    if (lower === 'voice') return 'voice';
    if (lower === 'text' || lower === 'number' || lower === 'decimal' || lower === 'boolean' || lower === 'date' || lower === 'time') {
      return lower as ConsultationFieldDefinition['fieldType'];
    }
  }

  switch (Number(value)) {
    case 1:
      return 'longText';
    case 2:
      return 'number';
    case 3:
      return 'boolean';
    case 4:
      return 'date';
    case 5:
      return 'time';
    case 6:
      return 'dateTime';
    case 7:
      return 'singleSelect';
    case 8:
      return 'multiSelect';
    case 9:
      return 'repeater';
    case 10:
      return 'attachments';
    case 11:
      return 'voice';
    default:
      return 'text';
  }
};

const normalizeConsultationFieldOptions = (value: any): ConsultationFieldDefinition['options'] => {
  const rawOptions = typeof value === 'string'
    ? (() => {
        try {
          return JSON.parse(value);
        } catch {
          return value
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
            .map((item, index) => ({ id: `legacy-${index + 1}`, label: item }));
        }
      })()
    : value;

  if (!Array.isArray(rawOptions)) {
    return undefined;
  }

  const options = rawOptions
    .map((item, index) => {
      if (typeof item === 'string') {
        const label = item.trim();
        if (!label) {
          return null;
        }

        return { id: `legacy-${index + 1}`, label };
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      const rawId = item.id ?? item.Id;
      const rawLabel = item.label ?? item.Label ?? item.value ?? item.Value ?? item.name ?? item.Name;

      const id = typeof rawId === 'string' ? rawId.trim() : '';
      const label = typeof rawLabel === 'string' ? rawLabel.trim() : '';
      if (!id || !label) {
        if (label) {
          return { id: `legacy-${index + 1}`, label };
        }

        return null;
      }

      return { id, label };
    })
    .filter((item): item is { id: string; label: string } => item !== null);

  return options.length > 0 ? options : undefined;
};

const normalizeAiStatus = (value: any): ConsultationFieldDefinition['aiStatus'] => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === 'pendingapproval') return 'pendingApproval';
  if (normalized === 'approved') return 'approved';
  if (normalized === 'rejected') return 'rejected';
  return undefined;
};

const normalizeConsultation = (value: any): Consultation => ({
  id: String(value?.id ?? value?.Id ?? ''),
  eventId: String(value?.eventId ?? ''),
  assetId: value?.assetId ? String(value.assetId) : undefined,
  contactId: value?.contactId ? String(value.contactId) : undefined,
  anamnesisSummary: value?.anamnesisSummary ? String(value.anamnesisSummary) : undefined,
  notes: value?.notes ? String(value.notes) : undefined,
  moduleKey: value?.moduleKey ? String(value.moduleKey) : undefined,
  domainProfileKey: value?.domainProfileKey ? String(value.domainProfileKey) : undefined,
  appointmentTypeId: value?.appointmentTypeId ? String(value.appointmentTypeId) : undefined,
  templateId: value?.templateId ? String(value.templateId) : undefined,
  status: value?.status ? String(value.status) : undefined,
  channel: value?.channel ? String(value.channel) : undefined,
  subjectType: value?.subjectType ? String(value.subjectType) : undefined,
  subjectId: value?.subjectId ? String(value.subjectId) : undefined,
  customerContactId: value?.customerContactId ? String(value.customerContactId) : undefined,
  assignedEmployeeId: value?.assignedEmployeeId ? String(value.assignedEmployeeId) : undefined,
  calendarEventId: value?.calendarEventId ? String(value.calendarEventId) : undefined,
  taskId: value?.taskId ? String(value.taskId) : undefined,
  conversationId: value?.conversationId ? String(value.conversationId) : undefined,
  externalReference: value?.externalReference ? String(value.externalReference) : undefined,
  scheduledAtUtc: value?.scheduledAtUtc ? String(value.scheduledAtUtc) : undefined,
  startedAtUtc: value?.startedAtUtc ? String(value.startedAtUtc) : undefined,
  completedAtUtc: value?.completedAtUtc ? String(value.completedAtUtc) : undefined,
  summary: value?.summary ? String(value.summary) : undefined,
  internalNotes: value?.internalNotes ? String(value.internalNotes) : undefined,
  outcomeType: value?.outcomeType ? String(value.outcomeType) : undefined,
  assetName: value?.assetName ? String(value.assetName) : undefined,
  contactName: value?.contactName ? String(value.contactName) : undefined,
  customerContactName: value?.customerContactName ? String(value.customerContactName) : undefined,
  appointmentTypeName: value?.appointmentTypeName || value?.AppointmentTypeName
    ? String(value?.appointmentTypeName ?? value?.AppointmentTypeName)
    : undefined,
  templateName: value?.templateName ? String(value.templateName) : undefined,
  calendarEventTitle: value?.calendarEventTitle ? String(value.calendarEventTitle) : undefined,
  createdByEmployeeId: value?.createdByEmployeeId ? String(value.createdByEmployeeId) : undefined,
  updatedByEmployeeId: value?.updatedByEmployeeId ? String(value.updatedByEmployeeId) : undefined,
  createdByEmployeeName: value?.createdByEmployeeName ? String(value.createdByEmployeeName) : undefined,
  updatedByEmployeeName: value?.updatedByEmployeeName ? String(value.updatedByEmployeeName) : undefined,
  createdByAi: Boolean(value?.createdByAi),
  aiStatus: normalizeAiStatus(value?.aiStatus),
  isDeleted: Boolean(value?.isDeleted),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeConsultationStatusDefinition = (value: any): ConsultationStatusDefinition => ({
  id: String(value?.id ?? value?.Id ?? ''),
  name: String(value?.name ?? value?.Name ?? ''),
  displayOrder: typeof (value?.displayOrder ?? value?.DisplayOrder) === 'number' ? (value?.displayOrder ?? value?.DisplayOrder) : 0,
  isDefault: Boolean(value?.isDefault ?? value?.IsDefault),
  isCompleted: Boolean(value?.isCompleted ?? value?.IsCompleted),
});

const normalizeConsultationCreationMode = (value: unknown): AppointmentType['consultationCreationMode'] => {
  const normalized = String(value ?? 'single').trim().toLowerCase();
  if (normalized === 'percontact') return 'perContact';
  if (normalized === 'perasset') return 'perAsset';
  if (normalized === 'peremployee') return 'perEmployee';
  return 'single';
};

const normalizeChatBotBookingFlow = (value: unknown): AppointmentType['chatBotBookingFlow'] => {
  const normalized = String(value ?? 'offerAvailableSlots').trim().toLowerCase();
  if (normalized === 'autonearestslot') return 'autoNearestSlot';
  return 'offerAvailableSlots';
};

const normalizeConsultationType = (value: any): AppointmentType => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  description: value?.description ? String(value.description) : undefined,
  defaultDurationMinutes: typeof value?.defaultDurationMinutes === 'number' ? value.defaultDurationMinutes : 0,
  color: value?.color ? String(value.color) : undefined,
  status: String(value?.status ?? 'active') as AppointmentType['status'],
  consultationCreationMode: normalizeConsultationCreationMode(value?.consultationCreationMode),
  chatBotBookingFlow: normalizeChatBotBookingFlow(value?.chatBotBookingFlow),
  chatBotInstruction: value?.chatBotInstruction ? String(value.chatBotInstruction) : undefined,
  sortOrder: typeof value?.sortOrder === 'number' ? value.sortOrder : 0,
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeConsultationTemplateField = (value: any): ConsultationTemplateField => ({
  id: String(value?.id ?? ''),
  templateId: String(value?.templateId ?? ''),
  fieldDefinitionId: String(value?.fieldDefinitionId ?? ''),
  sectionKey: value?.sectionKey ? String(value.sectionKey) : undefined,
  sortOrder: typeof value?.sortOrder === 'number' ? value.sortOrder : 0,
  isRequired: Boolean(value?.isRequired),
  createdAt: String(value?.createdAt ?? ''),
});

const normalizeConsultationFieldAssignment = (value: any): ConsultationFieldAssignment => ({
  id: String(value?.id ?? ''),
  consultationId: String(value?.consultationId ?? ''),
  fieldDefinitionId: String(value?.fieldDefinitionId ?? ''),
  sectionKey: value?.sectionKey ? String(value.sectionKey) : undefined,
  sortOrder: typeof value?.sortOrder === 'number' ? value.sortOrder : 0,
  isRequired: Boolean(value?.isRequired),
  createdAt: String(value?.createdAt ?? ''),
});

const normalizeConsultationTemplate = (value: any): ConsultationTemplate => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  code: value?.code ? String(value.code) : undefined,
  domainProfileKey: String(value?.domainProfileKey ?? ''),
  appointmentTypeId: value?.appointmentTypeId ? String(value.appointmentTypeId) : undefined,
  subjectType: value?.subjectType ? String(value.subjectType) : undefined,
  description: value?.description ? String(value.description) : undefined,
  isActive: Boolean(value?.isActive),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
  fields: Array.isArray(value?.fields) ? value.fields.map(normalizeConsultationTemplateField) : undefined,
  createdByAi: Boolean(value?.createdByAi),
  aiStatus: normalizeAiStatus(value?.aiStatus),
});

const normalizeConsultationFieldDefinition = (value: any): ConsultationFieldDefinition => ({
  id: String(value?.id ?? value?.Id ?? ''),
  name: String(value?.name ?? value?.Name ?? ''),
  fieldType: normalizeConsultationFieldType(value?.fieldType ?? value?.FieldType),
  description: value?.description ?? value?.Description ? String(value?.description ?? value?.Description) : undefined,
  placeholder: value?.placeholder ?? value?.Placeholder ? String(value?.placeholder ?? value?.Placeholder) : undefined,
  helpText: value?.helpText ?? value?.HelpText ? String(value?.helpText ?? value?.HelpText) : undefined,
  validationJson: value?.validationJson ?? value?.ValidationJson ? String(value?.validationJson ?? value?.ValidationJson) : undefined,
  uiSchemaJson: value?.uiSchemaJson ?? value?.UiSchemaJson ? String(value?.uiSchemaJson ?? value?.UiSchemaJson) : undefined,
  schemaJson: value?.schemaJson ?? value?.SchemaJson ? String(value?.schemaJson ?? value?.SchemaJson) : undefined,
  code: value?.code ?? value?.Code ? String(value?.code ?? value?.Code) : undefined,
  isRequired: Boolean(value?.isRequired ?? value?.IsRequired),
  defaultValue: value?.defaultValue ?? value?.DefaultValue ? String(value?.defaultValue ?? value?.DefaultValue) : undefined,
  options: normalizeConsultationFieldOptions(value?.options ?? value?.Options),
  sortOrder: typeof (value?.sortOrder ?? value?.SortOrder) === 'number' ? (value?.sortOrder ?? value?.SortOrder) : 0,
  isActive: Boolean(value?.isActive ?? value?.IsActive),
  createdAt: String(value?.createdAt ?? value?.CreatedAt ?? ''),
  updatedAt: String(value?.updatedAt ?? value?.UpdatedAt ?? ''),
  createdByAi: Boolean(value?.createdByAi ?? value?.CreatedByAi),
  aiStatus: normalizeAiStatus(value?.aiStatus ?? value?.AiStatus),
});

const normalizeConsultationFieldValue = (value: any): ConsultationFieldValue => ({
  id: String(value?.id ?? ''),
  consultationId: String(value?.consultationId ?? ''),
  fieldDefinitionId: String(value?.fieldDefinitionId ?? ''),
  value: value?.value !== undefined && value?.value !== null ? String(value.value) : undefined,
  sortOrder: typeof value?.sortOrder === 'number' ? value.sortOrder : 0,
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

export const consultationService = {
  listConsultationStatuses: async (): Promise<ConsultationStatusDefinition[]> => {
    const payload = await apiRequest<any>('/api/v1/consultations/statuses');
    return pickItems<any>(payload).map(normalizeConsultationStatusDefinition);
  },

  listConsultationTypes: async (filters: AppointmentTypeListFilters = {}): Promise<AppointmentType[]> => {
    const query = toQueryString({
      status: filters.status,
    });
    const payload = await apiRequest<any>(`/api/v1/consultations/types${query}`);
    return pickItems<any>(payload).map(normalizeConsultationType);
  },

  createConsultationType: async (input: CreateAppointmentTypeInput): Promise<AppointmentType | null> => {
    const payload = await apiRequest<any>('/api/v1/consultations/types', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationType(item) : null;
  },

  updateConsultationType: async (
    consultationTypeId: string,
    input: UpdateAppointmentTypeInput,
  ): Promise<AppointmentType | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/types/${consultationTypeId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationType(item) : null;
  },

  listConsultationsPage: async (filters: ConsultationListFilters = {}): Promise<PagedConsultationResult> => {
    const query = toQueryString({
      eventId: filters.eventId,
      assetId: filters.assetId,
      contactId: filters.contactId,
      customerContactId: filters.customerContactId,
      assignedEmployeeId: filters.assignedEmployeeId,
      appointmentTypeId: filters.appointmentTypeId,
      hasAppointmentType: filters.hasAppointmentType,
      templateId: filters.templateId,
      hasTemplate: filters.hasTemplate,
      status: filters.status,
      hasStatus: filters.hasStatus,
      search: filters.search,
      sort: filters.sort,
      page: filters.page,
      pageSize: filters.pageSize,
      includeDeleted: filters.includeDeleted,
    });
    const payload = await apiRequest<any>(`/api/v1/consultations${query}`);
    return pickPaged(payload, normalizeConsultation, filters.page ?? 1, filters.pageSize ?? 25);
  },

  listConsultations: async (filters: ConsultationListFilters = {}): Promise<Consultation[]> => {
    const aggregated: Consultation[] = [];
    let page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 100;

    while (true) {
      const current = await consultationService.listConsultationsPage({
        ...filters,
        page,
        pageSize,
      });

      aggregated.push(...current.items);

      if (!current.hasNext) {
        return aggregated;
      }

      page += 1;
    }
  },

  getConsultationById: async (consultationId: string): Promise<Consultation | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}`);
      const item = pickSingle<any>(payload);
      return item ? normalizeConsultation(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) return null;
      throw error;
    }
  },

  createConsultation: async (input: CreateConsultationInput): Promise<Consultation | null> => {
    const payload = await apiRequest<any>('/api/v1/consultations', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultation(item) : null;
  },

  updateConsultation: async (consultationId: string, input: UpdateConsultationInput): Promise<Consultation | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultation(item) : null;
  },

  deleteConsultation: async (consultationId: string): Promise<void> => {
    await apiRequest<any>(`/api/v1/consultations/${consultationId}`, {
      method: 'DELETE',
    });
  },

  approveConsultation: async (consultationId: string): Promise<Consultation | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}/approve`, {
      method: 'POST',
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultation(item) : null;
  },

  rejectConsultation: async (consultationId: string): Promise<Consultation | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}/reject`, {
      method: 'POST',
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultation(item) : null;
  },

  listConsultationFieldDefinitions: async (filters: ConsultationFieldDefinitionListFilters = {}): Promise<ConsultationFieldDefinition[]> => {
    const query = toQueryString({
      fieldType: filters.fieldType,
      isActive: filters.isActive,
    });
    const payload = await apiRequest<any>(`/api/v1/consultations/field-definitions${query}`);
    return pickItems<any>(payload).map(normalizeConsultationFieldDefinition);
  },

  getConsultationFieldDefinitionById: async (fieldDefinitionId: string): Promise<ConsultationFieldDefinition | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/consultations/field-definitions/${fieldDefinitionId}`);
      const item = pickSingle<any>(payload);
      return item ? normalizeConsultationFieldDefinition(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) return null;
      throw error;
    }
  },

  createConsultationFieldDefinition: async (input: CreateConsultationFieldDefinitionInput): Promise<ConsultationFieldDefinition | null> => {
    const payload = await apiRequest<any>('/api/v1/consultations/field-definitions', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationFieldDefinition(item) : null;
  },

  updateConsultationFieldDefinition: async (fieldDefinitionId: string, input: UpdateConsultationFieldDefinitionInput): Promise<ConsultationFieldDefinition | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/field-definitions/${fieldDefinitionId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationFieldDefinition(item) : null;
  },

  removeConsultationFieldDefinition: async (fieldDefinitionId: string): Promise<void> => {
    await apiRequest<any>(`/api/v1/consultations/field-definitions/${fieldDefinitionId}`, {
      method: 'DELETE',
    });
  },

  approveConsultationFieldDefinition: async (fieldDefinitionId: string): Promise<ConsultationFieldDefinition | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/field-definitions/${fieldDefinitionId}/approve`, {
      method: 'POST',
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationFieldDefinition(item) : null;
  },

  rejectConsultationFieldDefinition: async (fieldDefinitionId: string): Promise<ConsultationFieldDefinition | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/field-definitions/${fieldDefinitionId}/reject`, {
      method: 'POST',
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationFieldDefinition(item) : null;
  },

  listConsultationTemplates: async (filters: ConsultationTemplateListFilters = {}): Promise<ConsultationTemplate[]> => {
    const query = toQueryString({
      domainProfileKey: filters.domainProfileKey,
      appointmentTypeId: filters.appointmentTypeId,
      subjectType: filters.subjectType,
      isActive: filters.isActive,
      search: filters.search,
    });
    const payload = await apiRequest<any>(`/api/v1/consultation-templates${query}`);
    return pickItems<any>(payload).map(normalizeConsultationTemplate);
  },

  getConsultationTemplateById: async (templateId: string): Promise<ConsultationTemplate | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/consultation-templates/${templateId}`);
      const item = pickSingle<any>(payload);
      return item ? normalizeConsultationTemplate(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) return null;
      throw error;
    }
  },

  createConsultationTemplate: async (input: CreateConsultationTemplateInput): Promise<ConsultationTemplate | null> => {
    const payload = await apiRequest<any>('/api/v1/consultation-templates', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationTemplate(item) : null;
  },

  updateConsultationTemplate: async (templateId: string, input: UpdateConsultationTemplateInput): Promise<ConsultationTemplate | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultation-templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationTemplate(item) : null;
  },

  removeConsultationTemplate: async (templateId: string): Promise<void> => {
    await apiRequest<any>(`/api/v1/consultation-templates/${templateId}`, {
      method: 'DELETE',
    });
  },

  approveConsultationTemplate: async (templateId: string): Promise<ConsultationTemplate | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultation-templates/${templateId}/approve`, {
      method: 'POST',
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationTemplate(item) : null;
  },

  rejectConsultationTemplate: async (templateId: string): Promise<ConsultationTemplate | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultation-templates/${templateId}/reject`, {
      method: 'POST',
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationTemplate(item) : null;
  },

  addConsultationTemplateField: async (templateId: string, input: AddConsultationTemplateFieldInput): Promise<ConsultationTemplateField | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultation-templates/${templateId}/fields`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationTemplateField(item) : null;
  },

  updateConsultationTemplateField: async (templateId: string, fieldDefinitionId: string, input: UpdateConsultationTemplateFieldInput): Promise<ConsultationTemplateField | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultation-templates/${templateId}/fields/${fieldDefinitionId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationTemplateField(item) : null;
  },

  removeConsultationTemplateField: async (templateId: string, fieldDefinitionId: string): Promise<void> => {
    await apiRequest<any>(`/api/v1/consultation-templates/${templateId}/fields/${fieldDefinitionId}`, {
      method: 'DELETE',
    });
  },

  listConsultationFieldAssignments: async (consultationId: string): Promise<ConsultationFieldAssignment[]> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-assignments`);
      return pickItems<any>(payload).map(normalizeConsultationFieldAssignment);
    } catch {
      return [];
    }
  },

  addConsultationFieldAssignment: async (
    consultationId: string,
    input: AddConsultationFieldAssignmentInput,
  ): Promise<ConsultationFieldAssignment | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-assignments`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationFieldAssignment(item) : null;
  },

  removeConsultationFieldAssignment: async (consultationId: string, fieldDefinitionId: string): Promise<void> => {
    await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-assignments/${fieldDefinitionId}`, {
      method: 'DELETE',
    });
  },

  listConsultationFieldValues: async (consultationId: string): Promise<ConsultationFieldValue[]> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-values`);
    return pickItems<any>(payload).map(normalizeConsultationFieldValue);
  },

  addConsultationFieldValue: async (consultationId: string, input: AddConsultationFieldValueInput): Promise<ConsultationFieldValue | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-values`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationFieldValue(item) : null;
  },

  updateConsultationFieldValue: async (consultationId: string, fieldDefinitionId: string, input: UpdateConsultationFieldValueInput): Promise<ConsultationFieldValue | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-values/${fieldDefinitionId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationFieldValue(item) : null;
  },

  removeConsultationFieldValue: async (consultationId: string, fieldDefinitionId: string): Promise<void> => {
    await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-values/${fieldDefinitionId}`, {
      method: 'DELETE',
    });
  },

  uploadConsultationFieldAttachment: async (
    consultationId: string,
    fieldDefinitionId: string,
    file: File,
  ): Promise<ConsultationFieldValue> => {
    const formData = new FormData();
    formData.append('file', file);

    const payload = await apiRequest<any>(
      `/api/v1/consultations/${consultationId}/field-values/${fieldDefinitionId}/attachments`,
      {
        method: 'POST',
        body: formData,
      },
    );

    const item = pickSingle<any>(payload);
    if (!item) {
      throw new Error('Upload failed.');
    }

    return normalizeConsultationFieldValue(item);
  },

  deleteConsultationFieldAttachment: async (
    consultationId: string,
    fieldDefinitionId: string,
    attachmentId: string,
  ): Promise<ConsultationFieldValue> => {
    const payload = await apiRequest<any>(
      `/api/v1/consultations/${consultationId}/field-values/${fieldDefinitionId}/attachments/${attachmentId}`,
      {
        method: 'DELETE',
      },
    );

    const item = pickSingle<any>(payload);
    if (!item) {
      throw new Error('Delete failed.');
    }

    return normalizeConsultationFieldValue(item);
  },

  uploadConsultationFieldVoice: async (
    consultationId: string,
    fieldDefinitionId: string,
    file: File,
    durationSeconds?: number,
  ): Promise<ConsultationFieldValue> => {
    const formData = new FormData();
    formData.append('file', file);
    if (durationSeconds && durationSeconds > 0) {
      formData.append('durationSeconds', String(durationSeconds));
    }

    const payload = await apiRequest<any>(
      `/api/v1/consultations/${consultationId}/field-values/${fieldDefinitionId}/voice`,
      {
        method: 'POST',
        body: formData,
      },
    );

    const item = pickSingle<any>(payload);
    if (!item) {
      throw new Error('Upload failed.');
    }

    return normalizeConsultationFieldValue(item);
  },

  deleteConsultationFieldVoice: async (
    consultationId: string,
    fieldDefinitionId: string,
  ): Promise<ConsultationFieldValue> => {
    const payload = await apiRequest<any>(
      `/api/v1/consultations/${consultationId}/field-values/${fieldDefinitionId}/voice`,
      {
        method: 'DELETE',
      },
    );

    const item = pickSingle<any>(payload);
    if (!item) {
      throw new Error('Delete failed.');
    }

    return normalizeConsultationFieldValue(item);
  },

  transcribeConsultationFieldVoice: async (
    consultationId: string,
    fieldDefinitionId: string,
  ): Promise<ConsultationFieldValue> => {
    const payload = await apiRequest<any>(
      `/api/v1/consultations/${consultationId}/field-values/${fieldDefinitionId}/voice/transcribe`,
      {
        method: 'POST',
      },
    );

    const item = pickSingle<any>(payload);
    if (!item) {
      throw new Error('Transcription failed.');
    }

    return normalizeConsultationFieldValue(item);
  },
};

export const {
  addConsultationFieldAssignment,
  addConsultationFieldValue,
  addConsultationTemplateField,
  createConsultationType,
  createConsultationFieldDefinition,
  createConsultationTemplate,
  createConsultation,
  deleteConsultation,
  getConsultationById,
  getConsultationFieldDefinitionById,
  getConsultationTemplateById,
  listConsultationFieldAssignments,
  listConsultationFieldDefinitions,
  listConsultationFieldValues,
  listConsultationStatuses,
  listConsultationsPage,
  listConsultationTemplates,
  listConsultationTypes,
  listConsultations,
  approveConsultation,
  rejectConsultation,
  approveConsultationTemplate,
  rejectConsultationTemplate,
  approveConsultationFieldDefinition,
  rejectConsultationFieldDefinition,
  removeConsultationFieldAssignment,
  removeConsultationFieldDefinition,
  removeConsultationTemplate,
  removeConsultationTemplateField,
  removeConsultationFieldValue,
  uploadConsultationFieldAttachment,
  deleteConsultationFieldAttachment,
  uploadConsultationFieldVoice,
  deleteConsultationFieldVoice,
  transcribeConsultationFieldVoice,
  updateConsultationFieldDefinition,
  updateConsultationTemplate,
  updateConsultationTemplateField,
  updateConsultationType,
  updateConsultation,
  updateConsultationFieldValue,
} = consultationService;